import { ArrowOutwardIcon, HeadphoneIcon } from "@/assets/icons";
import ContainerWrapper from "@/components/ui/ContainerWrapper";
import { useAlbumsControllerHomepageAlbums } from "@/lib/lutely-api/gen/albums/albums";
import { Link } from "@tanstack/react-router";
import AutoScroll from "embla-carousel-auto-scroll";
import useEmblaCarousel from "embla-carousel-react";
import { motion, useInView } from "motion/react";
import { useRef } from "react";
import { FANS_COVERS_FALLBACK } from "../data";
import AlbumCard from "./AlbumCard";
import { useCarouselOpacity } from "./useCarouselOpacity";

const fadeIn = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.33, 1, 0.68, 1] as const },
  },
};

const staggerCta = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.08 },
  },
};

const fadeUpCta = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.33, 1, 0.68, 1] as const },
  },
};

export default function FeaturedAlbumsSection() {
  const { data: albums } = useAlbumsControllerHomepageAlbums();
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const displayAlbums =
    albums && albums.length > 0
      ? albums.map((a) => ({
          key: a.albumId,
          albumId: a.albumId,
          title: a.title,
          artworkUrl: a.artworkUrl,
          creator: a.creator,
        }))
      : FANS_COVERS_FALLBACK.map((c, i) => ({
          key: `fallback-${i}`,
          title: c.title,
          artworkUrl: c.image,
          creator: {
            creatorId: "",
            stageName: c.artist,
            slug: null,
            profilePictureUrl: null,
          },
        }));

  const slides =
    displayAlbums.length >= 15
      ? displayAlbums
      : displayAlbums.concat(
          displayAlbums.map((a) => ({ ...a, key: `${a.key}-dup` })),
        );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "center",
      slidesToScroll: 1,
      containScroll: false,
      dragFree: true,
      startIndex: Math.floor(slides.length / 2),
    },
    [
      AutoScroll({
        speed: 0.8,
        startDelay: 0,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
      }),
    ],
  );

  useCarouselOpacity(emblaApi);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-x-clip pt-16 pb-20 md:pt-[140px] md:pb-[160px]"
    >
      {/* Glow — bottom of z-stack, behind all content */}
      <img
        src="/images/featured/album-carousel-bg.png"
        alt=""
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[1256px] w-[1256px]"
        aria-hidden="true"
      />

      {/* All content above glow */}
      <div className="relative z-10 flex flex-col items-center gap-10 md:gap-20">
        {/* Heading — fade in on scroll */}
        <ContainerWrapper maxWidth="1400px">
          <motion.div
            className="flex flex-col items-center gap-2 pb-5 text-center"
            variants={fadeIn}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <h2 className="font-figtree text-[28px] md:text-[48px] lg:text-[66px] font-black uppercase leading-[1.1] tracking-[-0.66px] text-blue-900">
              BUY ONCE, LISTEN FOREVER
            </h2>
            <p className="font-open-sans text-[18px] md:text-lg lg:text-[20px] font-semibold leading-[1.4] text-[#464C59]">
              When you buy an artist&apos;s album, you can listen to it forever.
              No monthly subscriptions to pay.
            </p>
          </motion.div>
        </ContainerWrapper>

        {/* Carousel — autoplay 3s, stop on hover, draggable */}
        <div className="relative w-full">
          <div className="relative overflow-clip">
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-[80px] md:w-[200px] bg-gradient-to-r from-[#fafbfd] to-transparent" />
            <div
              ref={emblaRef}
              className="overflow-hidden"
              role="region"
              aria-roledescription="carousel"
              aria-label="Featured albums"
            >
              <div className="flex">
                {slides.map((album) => (
                  <div
                    key={album.key}
                    className="min-w-0 flex-[0_0_260px] md:flex-[0_0_300px] px-2 md:px-4"
                  >
                    <AlbumCard album={album} />
                  </div>
                ))}
              </div>
            </div>
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-[80px] md:w-[200px] bg-gradient-to-l from-[#fafbfd] to-transparent" />
          </div>
        </div>

        {/* CTA — staggered fade in (button + link together) */}
        <motion.div
          className="flex flex-col items-center gap-2"
          variants={staggerCta}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.div variants={fadeUpCta}>
            <Link to="/marketplace/albums">
              <div className="rounded-full bg-teal-100 p-2">
                <div className="rounded-full bg-teal-300 p-2">
                  <div className="flex h-12 md:h-16 items-center justify-center gap-2 rounded-full bg-teal-900 px-5 md:px-6 hover:brightness-90 transition-all">
                    <HeadphoneIcon className="size-5 md:size-7 shrink-0 text-white" />
                    <span className="whitespace-nowrap font-open-sans text-[18px] font-bold leading-[1.4] text-white">
                      Explore Albums
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
          <motion.div variants={fadeUpCta}>
            <Link
              to="/artists"
              className="flex flex-col md:flex-row items-center gap-0 md:gap-1 font-open-sans text-base font-bold leading-[1.4] text-blue-900/70 hover:underline"
            >
              <span>Are you an artist?</span>
              <span className="flex items-center gap-1">
                Release your album on Lutely
                <ArrowOutwardIcon className="size-6 shrink-0" />
              </span>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
